import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  activeTakeoverQueryOptions,
  observeTakeoverStream,
  pendingInputsQueryOptions,
  queueTakeoverCommand,
  settleTakeover,
  takeoverStreamUrl,
  type ActiveTakeover,
} from '@/lib/api/quality.js';
import type {
  DeviceTakeoverRequest,
  TakeoverCommandResult,
  TakeoverState,
} from '@/lib/api/quality-types.js';
import {
  classifyGesture,
  describeGesture,
  goalOf,
  toDevicePoints,
  type DevicePoint,
} from './deviceTakeoverGesture.js';
import { TakeoverTypingRow } from './DeviceTakeoverTyping.js';
import { KIND_LABEL } from './help-request-kind-label.js';
import {
  boundaryFromContentType,
  TakeoverMultipartParser,
  type TakeoverStreamFramePart,
} from './takeoverStream.js';

/**
 * How long connect() waits for a frame or heartbeat part before treating the connection as stalled and
 * tearing it down. Neither `cti-quality-service` (`TakeoverService`, `TakeoverStreamController`) nor
 * `cti-quality-worker` emits a heartbeat part today — the heartbeat kind this file's parser understands
 * is forward-compatible, not something a real cadence exists to derive "3x heartbeat interval" from.
 * Kept at the value already proven in this codebase, chosen with margin over what IS documented
 * server-side: `TakeoverStreamController`'s `PUSHED_FRAME_WAIT_MS = 15_000` (the server itself ends an
 * idle pushed-frame response after 15 s) and the "4-15 s" farm stream-cycling the same controller
 * documents. Below 15 s this watchdog would race the server's own EOF path; 22 s clears it with margin.
 */
const STREAM_LIVENESS_TIMEOUT_MS = 22_000;

/**
 * Capped exponential backoff between reconnect attempts (1s, 2s, 4s, 8s, then held at the 10s cap).
 * Reconnects continue indefinitely while the takeover stays actionable — a dead frozen frame with no
 * indication is worse than a visible, if slow, recovery (spec addendum, 2026-08-25 L'Oreal follow-up).
 * Only `open` turning false (submitted/expired/gave-up/blocked/cancelled) or the component unmounting
 * stops the loop, both of which already tear the owning effect down via its dependency array/cleanup.
 */
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 10_000;
function reconnectDelayMs(attempt: number): number {
  return Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** attempt,
    RECONNECT_MAX_DELAY_MS,
  );
}

const RECONNECTING_NOTICE = 'Reconnecting the live view…';

export function arrayBufferOf(bytes: Uint8Array<ArrayBufferLike>): ArrayBuffer {
  if (
    bytes.buffer instanceof ArrayBuffer &&
    bytes.byteOffset === 0 &&
    bytes.byteLength === bytes.buffer.byteLength
  ) {
    return bytes.buffer;
  }
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function isCaptchaTakeover(takeover: DeviceTakeoverRequest): boolean {
  return [takeover.target_key, takeover.key]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => /(^|[._:\-\s])captcha($|[._:\-\s])/i.test(value));
}

function reconnectLogLine(
  livenessExpired: boolean,
  renderError: unknown,
): string {
  if (livenessExpired) {
    return 'live view stopped sending frames — reconnecting';
  }
  if (renderError) {
    return 'live view frame failed to render — reconnecting';
  }
  return 'live view dropped — reconnecting';
}

/**
 * Merge the list row's own data (always present, refreshed every 5 s) with the run-scoped 1 s poll
 * (only fetched while expanded — see DeviceTakeoverRow). The list row already carries the full
 * takeover shape (block B unifies the two serializers), so this is purely "prefer fresher data when
 * we have it", never a fallback to an incomplete shape.
 *
 * Guarded by request_id: `activeTakeoverQueryOptions` is run-scoped, not row-scoped, so if it ever
 * answered with a DIFFERENT takeover than the one this row renders, using it would show the human the
 * wrong device. `state: 'idle'` means "nothing to add" for the same reason a probe with no data would.
 */
function mergeLive(
  request: DeviceTakeoverRequest,
  live: ActiveTakeover | undefined,
): DeviceTakeoverRequest {
  if (
    !live ||
    live.state === 'idle' ||
    live.request_id !== request.request_id
  ) {
    return request;
  }
  return { ...request, ...live, state: live.state as TakeoverState };
}

/** A predicate-less turn closes only when the worker observes a real page/device change. The
 *  worker uses that rule for every transport: web carries `viewport` + `frame`, mobile screenshots
 *  carry `device.screen` + `frame`, and the farm MJPEG path may carry no `frame` at all. The row is
 *  already gated on a live stream, so the missing predicate is the contract signal. */
function usesObservedEffect(takeover: DeviceTakeoverRequest): boolean {
  return !takeover.verify;
}

/** One line describing what happened, for every state that is not "still being driven". Written in
 *  words, not a raw enum value: the row must never silently disappear when a takeover ends, whatever
 *  ended it (spec B5). `final_state === null` while `state === 'expired'` is a service-restart orphan,
 *  not a genuine hold-window expiry — see DeviceTakeoverRequest's doc comment for why those two cases
 *  produce the same `state` but must read differently to the human. */
function statusLineOf(takeover: DeviceTakeoverRequest): string | null {
  switch (takeover.state) {
    case 'pending':
      return null;
    case 'submitted':
      return usesObservedEffect(takeover)
        ? 'Submitted — the agent is checking whether your gesture caused a visible change.'
        : 'Submitted — the agent is re-reading the device to verify the goal.';
    case 'answered':
      return 'Verified — the case continues.';
    case 'blocked':
      return 'Not reached — the case ends blocked.';
    case 'gave_up':
      return 'You gave up — the case ends blocked.';
    case 'expired':
      return takeover.final_state === null
        ? 'This takeover ended when the service restarted — raise it again.'
        : 'The hold window expired — the case is blocked, the run continues.';
    case 'cancelled':
      return 'The run was cancelled.';
    default:
      return null;
  }
}

/** `mm:ss` time left before `expiresAt`, or null once it has passed. `now` is a parameter rather than
 *  a `Date.now()` call inside the function so the render stays a pure function of its inputs — the
 *  live tick comes from the caller re-rendering on the row's existing 1 s poll, not from a timer here
 *  (D3: no new timer library, no new query). Null, not a negative number, is what lets the caller fall
 *  back to the expired state the next poll confirms (statusLineOf's 'expired' case). */
function remainingLabelOf(expiresAt: string, now: number): string | null {
  const expiresAtMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiresAtMs)) return null;
  const remainingMs = expiresAtMs - now;
  if (remainingMs <= 0) return null;
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function gestureResultLine(result: TakeoverCommandResult): string {
  if (result.error) return `gesture result is uncertain: ${result.error}`;
  if (!result.executed) return 'gesture failed: not executed';
  return result.changed
    ? 'gesture changed the screen'
    : 'gesture ran but changed NOTHING — treat it as failed';
}

/**
 * One device-takeover row inside the unified help block (InputRequiredPanel).
 *
 * Where a value row asks the human to type an answer, this asks them to drive the device — the agent
 * is stuck on a control it cannot move, so a human moves it while the run waits. Collapsed by default
 * when other help requests share the list (a stream must not run in the background while someone is
 * typing a password into a neighbouring row); auto-expands when it is the only one, since a human
 * arriving from a Teams deep link should see the device immediately.
 *
 * Two properties this component must not quietly break:
 *  - every gesture goes to the SERVICE, which queues it for the worker to execute on the session it
 *    already owns. The browser never touches the device, so each human action stays a command we issued
 *    and can therefore be recorded and replayed later;
 *  - pressing Submit decides nothing. The worker re-reads the declared predicate, or checks for a visible
 *    page/device change on a predicate-less action. Only its verdict can end the takeover, so this row
 *    never reports success on its own.
 */
export function DeviceTakeoverRow({
  runId,
  request,
  autoExpand,
  blockedByOtherHelp,
}: {
  runId: string;
  request: DeviceTakeoverRequest;
  autoExpand: boolean;
  blockedByOtherHelp: boolean;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(autoExpand);
  /** The SET/GOAL/NOW verify contract reads as cryptic jargon next to the plain instruction a human
   *  needs, so it stays collapsed until the human asks for it — the instruction is the headline, this
   *  is supporting detail (owner-approved layout, 2026-08-24 L'Oreal feedback). */
  const [verifyDetailsOpen, setVerifyDetailsOpen] = useState(false);
  // Re-expand whenever this becomes the sole open request, even if it wasn't when the row first
  // mounted (e.g. a neighbouring request just settled and dropped out of the list).
  useEffect(() => {
    if (blockedByOtherHelp) {
      setExpanded(false);
    } else if (autoExpand) {
      setExpanded(true);
    }
  }, [autoExpand, blockedByOtherHelp]);

  // Only polled while expanded: a collapsed row has nothing that needs 1 s freshness, and the list's
  // own 5 s doorbell already keeps its summary (expectation, status line) current. `enabled` combines
  // WITH the base options' own `id.length > 0` guard rather than replacing it — a plain override would
  // silently drop that guard for an empty runId.
  const baseTakeoverOptions = activeTakeoverQueryOptions(runId);
  // `dataUpdatedAt` is read (not just `data`) so the countdown below re-renders on every poll tick
  // even when the poll's payload is byte-identical to the last one — react-query's structural
  // sharing would otherwise keep the same `data` reference and skip the render. Before the first
  // poll resolves (dataUpdatedAt === 0) fall back to the wall clock.
  const {
    data: live,
    dataUpdatedAt,
    isFetchedAfterMount,
  } = useQuery({
    ...baseTakeoverOptions,
    enabled:
      expanded && !blockedByOtherHelp && (baseTakeoverOptions.enabled ?? true),
  });
  const serverTakeover = mergeLive(request, live);
  const [localState, setLocalState] = useState<TakeoverState | null>(null);
  const takeover = localState
    ? { ...serverTakeover, state: localState }
    : serverTakeover;
  const cachedIdle = live?.state === 'idle';
  const activeGone = cachedIdle && isFetchedAfterMount;
  const activeChecking = cachedIdle && !isFetchedAfterMount;

  useEffect(() => {
    if (serverTakeover.state !== 'pending') setLocalState(null);
  }, [serverTakeover.state]);

  // The 1 s active probe is authoritative. If it says the takeover is gone while the 5 s list still
  // has a pending row, freeze this stale row immediately and pull the doorbell query forward.
  useEffect(() => {
    if (!activeGone) return;
    void queryClient.invalidateQueries({
      queryKey: pendingInputsQueryOptions(runId).queryKey,
    });
  }, [activeGone, queryClient, runId]);
  const remaining =
    takeover.state === 'pending'
      ? remainingLabelOf(takeover.expires_at, dataUpdatedAt || Date.now())
      : null;

  const [log, setLog] = useState<string[]>([]);
  const [inFlightCommandId, setInFlightCommandId] = useState<string | null>(
    null,
  );
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fromRef = useRef<DevicePoint | null>(null);
  const trailRef = useRef<DevicePoint[]>([]);
  const [streamNotice, setStreamNotice] = useState<string | null>(null);
  const [legacyStreamEpoch, setLegacyStreamEpoch] = useState(0);
  /** Reconnect attempts since the stream last started, on the SAME capped-backoff sequence the v2
   *  canvas path uses (`reconnectDelayMs`). The legacy `<img>` transport has no per-part signal (no
   *  seq, no heartbeat) to confirm health mid-stream, so unlike the v2 path this counter is not reset
   *  on a later success — it only resets when `streamSrc` changes (a fresh takeover/token). */
  const legacyReconnectAttemptRef = useRef(0);
  const legacyBackoffTimerRef = useRef<number | null>(null);
  const usesV2Stream = Boolean(takeover.frame);
  const streamSrc = takeover.stream_token
    ? takeoverStreamUrl(
        takeover.stream_token,
        usesV2Stream ? { version: 'v2' } : {},
      )
    : null;
  const legacyStreamSrc =
    streamSrc && !usesV2Stream
      ? `${streamSrc}&e=${legacyStreamEpoch}`
      : streamSrc;
  const verifiesObservedEffect = usesObservedEffect(takeover);

  const open =
    takeover.state === 'pending' &&
    !activeGone &&
    !activeChecking &&
    !blockedByOtherHelp;
  const takeoverStatus = activeGone
    ? 'The takeover has ended — refreshing the run state.'
    : activeChecking
      ? 'Checking the current takeover state…'
      : statusLineOf(takeover);

  const note = (line: string): void =>
    setLog((prev) =>
      [`${new Date().toLocaleTimeString()}  ${line}`, ...prev].slice(0, 40),
    );

  const refreshTakeoverState = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: activeTakeoverQueryOptions(runId).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: pendingInputsQueryOptions(runId).queryKey,
      }),
    ]);
  };

  useEffect(() => {
    if (!inFlightCommandId) return;
    const result = (takeover.results ?? []).find(
      (candidate) => candidate.commandId === inFlightCommandId,
    );
    if (!result) return;
    setInFlightCommandId(null);
    note(gestureResultLine(result));
  }, [inFlightCommandId, takeover.results]);

  /** A live read of `open` for callbacks scheduled inside a closure that would otherwise only ever
   *  see the `open` value from the render that created it — the legacy backoff timer, and every
   *  point in the v2 canvas loop that sets `streamNotice` (heartbeat, a rendered frame, scheduling
   *  the next reconnect). `cancelled` (effect-instance-scoped) already covers the same "no longer
   *  current" case for the v2 loop's control flow; this is the belt-and-suspenders check specifically
   *  for `open`, since a stale in-flight iteration reading already-extracted stream parts has no
   *  other synchronous point to observe that `open` just turned false. */
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    legacyReconnectAttemptRef.current = 0;
    setLegacyStreamEpoch(0);
    setStreamNotice(null);
    return () => {
      if (legacyBackoffTimerRef.current !== null) {
        window.clearTimeout(legacyBackoffTimerRef.current);
        legacyBackoffTimerRef.current = null;
      }
    };
  }, [streamSrc, usesV2Stream]);

  /** The takeover can leave its actionable state (submitted, expired, gave up, blocked) while a
   *  legacy retry is already scheduled — without this, the timer keeps flipping `legacyStreamEpoch`
   *  and retrying every backoff interval until the row unmounts, for a stream nobody can act on
   *  anymore. Mirrors the stop condition the v2 canvas path already gets for free from `open` being
   *  in its effect's dependency array, and also clears a "Reconnecting…" notice left showing from
   *  either transport — otherwise it would keep reading as still-in-progress. */
  useEffect(() => {
    if (open) return;
    setStreamNotice(null);
    if (legacyBackoffTimerRef.current !== null) {
      window.clearTimeout(legacyBackoffTimerRef.current);
      legacyBackoffTimerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (
      !usesV2Stream ||
      !expanded ||
      blockedByOtherHelp ||
      !streamSrc ||
      !open
    ) {
      return;
    }
    let cancelled = false;
    let controller: AbortController | null = null;
    let backoffTimer: number | null = null;
    let activeConnectionId = 0;
    let lastSeq = -1;
    /** Reconnect attempts since the last confirmed-healthy signal (a rendered frame or a heartbeat).
     *  Drives both the observed `reconnect_count` and the backoff delay; reset on any such signal so a
     *  takeover that stalls repeatedly over a long hold window is not left pinned at the 10s cap. */
    let attempt = 0;
    let painting = false;
    let queuedFrame: {
      connectionId: number;
      failConnection: (error: unknown) => void;
      part: TakeoverStreamFramePart;
      receivedAt: string;
    } | null = null;

    const observe = (
      observation: Parameters<typeof observeTakeoverStream>[2],
    ): void => {
      void Promise.resolve(
        observeTakeoverStream(runId, takeover.request_id, observation),
      ).catch(() => undefined);
    };

    const paintQueued = async (): Promise<void> => {
      if (painting || !queuedFrame) return;
      painting = true;
      const next = queuedFrame;
      queuedFrame = null;
      try {
        const canvas = canvasRef.current;
        if (
          !canvas ||
          next.connectionId !== activeConnectionId ||
          next.part.seq <= lastSeq
        )
          return;
        const bitmap = await createImageBitmap(
          new Blob([arrayBufferOf(next.part.bytes)], {
            type: next.part.contentType,
          }),
        );
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );
        if (cancelled) {
          bitmap.close?.();
          return;
        }
        if (next.connectionId !== activeConnectionId) {
          bitmap.close?.();
          return;
        }
        if (!(canvas instanceof HTMLCanvasElement)) {
          bitmap.close?.();
          throw new Error('Live view canvas was unavailable');
        }
        const context = canvas.getContext('2d');
        if (!context) {
          bitmap.close?.();
          throw new Error('Live view canvas context was unavailable');
        }
        try {
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          context.drawImage(bitmap, 0, 0);
        } finally {
          bitmap.close?.();
        }
        lastSeq = next.part.seq;
        attempt = 0;
        if (openRef.current) setStreamNotice(null);
        observe({
          event: 'frame_rendered',
          request_id: takeover.request_id,
          occurred_at: new Date().toISOString(),
          seq: next.part.seq,
          captured_at: next.part.capturedAt,
          published_at: next.part.publishedAt,
          received_at: next.receivedAt,
          rendered_at: new Date().toISOString(),
        });
      } catch (error) {
        queuedFrame = null;
        next.failConnection(error);
      } finally {
        painting = false;
        if (queuedFrame) void paintQueued();
      }
    };

    const queueFrameForPaint = (
      connectionId: number,
      failConnection: (error: unknown) => void,
      part: TakeoverStreamFramePart,
      receivedAt: string,
    ): void => {
      queuedFrame = {
        connectionId,
        failConnection,
        part,
        receivedAt,
      };
      void paintQueued();
    };

    const connect = async (): Promise<void> => {
      const thisController = new AbortController();
      let livenessTimer: number | null = null;
      let livenessExpired = false;
      let renderError: unknown = null;
      controller = thisController;
      const connectionId = activeConnectionId + 1;
      activeConnectionId = connectionId;
      const failThisConnection = (error: unknown): void => {
        renderError = error;
        thisController.abort();
      };
      const clearLivenessTimer = (): void => {
        if (livenessTimer !== null) window.clearTimeout(livenessTimer);
        livenessTimer = null;
      };
      const resetLivenessTimer = (): void => {
        clearLivenessTimer();
        livenessTimer = window.setTimeout(() => {
          livenessExpired = true;
          thisController.abort();
        }, STREAM_LIVENESS_TIMEOUT_MS);
      };
      /** Schedules the next attempt through the same capped-backoff sequence, indefinitely — the
       *  effect's own `open` gate (via its dependency array/cleanup) is what stops this, not a retry
       *  ceiling here. A live, if unglamorous, notice replaces the old terminal "refresh the page". */
      const scheduleReconnect = (logLine: string): void => {
        if (cancelled || !openRef.current) return;
        note(logLine);
        setStreamNotice(RECONNECTING_NOTICE);
        const delay = reconnectDelayMs(attempt);
        attempt += 1;
        backoffTimer = window.setTimeout(() => {
          backoffTimer = null;
          if (cancelled) return;
          void connect();
        }, delay);
      };
      observe({
        event: attempt === 0 ? 'stream_open' : 'stream_reconnect',
        request_id: takeover.request_id,
        occurred_at: new Date().toISOString(),
        reconnect_count: attempt,
      });
      try {
        resetLivenessTimer();
        // Re-derived from the render-scope `streamSrc` on every attempt: a token rotation refreshes
        // `takeover.stream_token` through the existing 1s active-takeover poll, which changes
        // `streamSrc` and tears this whole effect down (dependency array) before it ever gets here —
        // so an in-flight backoff always reconnects against the current token, never a stale one.
        const response = await fetch(streamSrc, {
          signal: thisController.signal,
          cache: 'no-store',
          headers: { Accept: 'multipart/x-mixed-replace' },
        });
        if (!response.ok || !response.body) {
          throw new Error(`stream answered ${response.status}`);
        }
        const boundary = boundaryFromContentType(
          response.headers.get('content-type'),
        );
        const parser = new TakeoverMultipartParser(boundary);
        const reader = response.body.getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          for (const part of parser.push(value)) {
            if (part.requestId !== takeover.request_id) {
              continue;
            }
            if (part.kind === 'heartbeat') {
              if (part.seq === null) continue;
              resetLivenessTimer();
              attempt = 0;
              if (openRef.current) setStreamNotice(null);
              observe({
                event: 'heartbeat',
                request_id: takeover.request_id,
                occurred_at: new Date().toISOString(),
                seq: part.seq,
                published_at: part.publishedAt,
              });
              continue;
            }
            if (part.seq <= lastSeq) continue;
            resetLivenessTimer();
            const receivedAt = new Date().toISOString();
            observe({
              event: 'frame_received',
              request_id: takeover.request_id,
              occurred_at: receivedAt,
              seq: part.seq,
              captured_at: part.capturedAt,
              published_at: part.publishedAt,
              received_at: receivedAt,
            });
            queueFrameForPaint(
              connectionId,
              failThisConnection,
              part,
              receivedAt,
            );
          }
        }
        for (const part of parser.finish()) {
          if (
            part.kind === 'frame' &&
            part.requestId === takeover.request_id &&
            part.seq > lastSeq
          ) {
            queueFrameForPaint(
              connectionId,
              failThisConnection,
              part,
              new Date().toISOString(),
            );
          }
        }
        clearLivenessTimer();
        if (cancelled) return;
        observe({
          event: 'stream_eof',
          request_id: takeover.request_id,
          occurred_at: new Date().toISOString(),
          reconnect_count: attempt,
        });
        scheduleReconnect('live view ended — reconnecting');
      } catch {
        clearLivenessTimer();
        if (
          cancelled ||
          (thisController.signal.aborted && !livenessExpired && !renderError)
        )
          return;
        observe({
          event: 'stream_error',
          request_id: takeover.request_id,
          occurred_at: new Date().toISOString(),
          reconnect_count: attempt,
        });
        scheduleReconnect(reconnectLogLine(livenessExpired, renderError));
      }
    };

    setStreamNotice(null);
    void connect();
    return () => {
      cancelled = true;
      controller?.abort();
      if (backoffTimer !== null) window.clearTimeout(backoffTimer);
      // Self-contained rather than relying only on the separate `[open]`-keyed effect below: this
      // cleanup also fires when the row collapses or gets blocked by other help (dependencies this
      // effect has that that other effect does not), and a stale "Reconnecting…" notice must not
      // survive either of those the same way it must not survive `open` turning false.
      setStreamNotice(null);
    };
  }, [
    blockedByOtherHelp,
    expanded,
    open,
    runId,
    streamSrc,
    takeover.request_id,
    usesV2Stream,
  ]);

  useEffect(() => {
    if (open) return;
    fromRef.current = null;
    trailRef.current = [];
    setInFlightCommandId(null);
  }, [open]);

  const gesture = useMutation({
    mutationFn: (body: Parameters<typeof queueTakeoverCommand>[2]) =>
      queueTakeoverCommand(runId, takeover.request_id, body),
    onSuccess: (result) => {
      setInFlightCommandId(result.commandId);
      void refreshTakeoverState();
    },
    onError: (error: Error) => {
      setInFlightCommandId(null);
      note(`refused: ${error.message}`);
      void refreshTakeoverState();
    },
  });

  const gestureBusy =
    open &&
    (gesture.isPending ||
      inFlightCommandId !== null ||
      takeover.pending_commands > 0);
  const status = gestureBusy
    ? 'Applying gesture… Wait for the updated live view before trying again.'
    : takeoverStatus;

  const settle = useMutation({
    mutationFn: (kind: 'submit' | 'give_up') =>
      settleTakeover(runId, takeover.request_id, kind),
    onMutate: (kind) =>
      setLocalState(kind === 'submit' ? 'submitted' : 'gave_up'),
    onSuccess: async (_result, kind) => {
      note(
        kind === 'submit'
          ? verifiesObservedEffect
            ? 'submitted — the agent now checks whether the gesture caused a visible change'
            : 'submitted — the agent now verifies the expected state by re-reading the device'
          : 'gave up — the case will be reported blocked',
      );
      await refreshTakeoverState();
    },
    onError: (error: Error) => {
      setLocalState(null);
      note(`refused: ${error.message}`);
      void refreshTakeoverState();
    },
  });

  /**
   * A takeover with no live view is not rendered at all — the owner's rule, after a real user was shown
   * one: a card asking a human to drive a device they cannot see, with an unknown current value, is
   * worse than nothing on this screen. The service already filters these out of the list; this is the
   * defensive half, so a stream that dies between two polls cannot leave a useless card behind. Placed
   * after every hook, so the hook order never changes with it.
   */
  if (!takeover.has_stream || !takeover.stream_token) return null;

  const goal = goalOf(takeover.verify ?? null, takeover.observed ?? null, {
    observedEffectFallback: verifiesObservedEffect,
  });
  /** The instruction is the headline a human must act on; `reason` (what the agent tried and what
   *  happened) is secondary context. An empty `expectation` falls back to `reason` as the headline
   *  rather than showing nothing. */
  const hasExpectation = takeover.expectation.trim().length > 0;
  const headline = hasExpectation ? takeover.expectation : takeover.reason;
  /** Unique per row so `aria-controls` resolves to the right disclosure when several help requests
   *  (and therefore several DeviceTakeoverRow instances) render in the same list. */
  const verifyBodyId = `takeover-verify-body-${takeover.request_id}`;

  const pointOf = (
    event: React.PointerEvent<HTMLCanvasElement | HTMLImageElement>,
  ): DevicePoint | null => {
    const point = toDevicePoints({
      clientX: event.clientX,
      clientY: event.clientY,
      rect: event.currentTarget.getBoundingClientRect(),
      screen: takeover.device?.screen ?? null,
      viewport: takeover.viewport ?? null,
      frame: takeover.frame ?? null,
    });
    if (!point)
      note(
        'this takeover carries neither a page size nor a device size — refusing to guess coordinates',
      );
    return point;
  };

  const handlePointerDown = (
    event: React.PointerEvent<HTMLCanvasElement | HTMLImageElement>,
  ): void => {
    event.preventDefault();
    if (!open || gestureBusy) return;
    fromRef.current = pointOf(event);
    trailRef.current = [];
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (
    event: React.PointerEvent<HTMLCanvasElement | HTMLImageElement>,
  ): void => {
    if (!fromRef.current) return;
    const point = pointOf(event);
    if (point) trailRef.current.push(point);
  };

  const handlePointerCancel = (): void => {
    if (fromRef.current)
      note('the browser cancelled the gesture — nothing was sent');
    fromRef.current = null;
    trailRef.current = [];
  };

  const handlePointerUp = (
    event: React.PointerEvent<HTMLCanvasElement | HTMLImageElement>,
  ): void => {
    if (!fromRef.current) return;
    const from = fromRef.current;
    const moves = trailRef.current;
    fromRef.current = null;
    trailRef.current = [];
    if (!open) {
      note('the takeover ended during the gesture — nothing was sent');
      return;
    }
    const command = classifyGesture({
      from,
      moves,
      to: pointOf(event),
    });
    if (!command) {
      note('gesture had no usable endpoint — nothing sent');
      return;
    }
    note(`${describeGesture(command)} queued`);
    gesture.mutate(command);
  };

  return (
    <li
      className="flex flex-col gap-2 rounded-md border border-border bg-background p-3"
      data-testid="takeover-row"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => {
          if (!blockedByOtherHelp) setExpanded((prev) => !prev);
        }}
        data-testid="takeover-toggle"
        aria-expanded={expanded && !blockedByOtherHelp}
        disabled={blockedByOtherHelp}
        title={
          blockedByOtherHelp
            ? 'Resolve the other help request before opening the live device.'
            : undefined
        }
      >
        {expanded ? (
          <ChevronDownIcon
            className="h-4 w-4 shrink-0 opacity-60"
            aria-hidden
          />
        ) : (
          <ChevronRightIcon
            className="h-4 w-4 shrink-0 opacity-60"
            aria-hidden
          />
        )}
        <Badge variant="outline">{KIND_LABEL.device_takeover}</Badge>
        <span className="flex-1 truncate text-[12px]">{headline}</span>
        {!expanded && status ? (
          <span className="shrink-0 text-[11px] opacity-70">{status}</span>
        ) : null}
      </button>

      {expanded && !blockedByOtherHelp ? (
        <div className="flex flex-col gap-3" data-testid="takeover-body">
          {/* The instruction is the headline — a real user missed a sentence-shaped target entirely
              because it read as one small line under a loud SET/GOAL/NOW box (owner feedback, 2026-08-24
              L'Oreal run). `reason` (what the agent tried and what happened) stays as plain secondary
              text right under it. */}
          <p
            className="text-xl font-semibold leading-snug"
            data-testid="takeover-headline"
          >
            {headline}
          </p>
          {hasExpectation ? (
            <p className="text-xs opacity-70" data-testid="takeover-reason">
              {takeover.reason}
            </p>
          ) : null}

          {
            /* The frame lives in a box whose size comes from the streamed FRAME, not from the image data. Sized by
               the image, the panel collapsed to nothing every time a stream dropped: the picture vanished, the page
               jumped, the next stream painted in top-down, and the pointer capture died mid-drag — the whole
               surface became unusable (reported by the first real user, 2026-08-04). A reserved box keeps the
               layout still and the mouse target fixed no matter what the stream does. */
            <div
              data-testid="takeover-frame-box"
              className="relative overflow-hidden rounded border bg-black/5"
              style={{
                /* HEIGHT drives this box and the aspect ratio derives the width — never the other way
                   round. With `aspect-ratio` + `max-height` alone the box has no width source of its own,
                   so it inherits one from its parent's cross-axis sizing. That worked while this was a
                   top-level <section>; inside the help block's flex-column <li> it collapsed to
                   `width: 0` and the human saw an empty gap while the stream was perfectly healthy —
                   measured in a real browser, 2026-08-05: naturalWidth 1170, boxW 0. Fixing it by height
                   keeps the box independent of wherever the row is mounted next. */
                height: '70vh',
                aspectRatio: `${takeover.frame?.width ?? takeover.viewport?.width ?? takeover.device?.screen?.width ?? 390} / ${takeover.frame?.height ?? takeover.viewport?.height ?? takeover.device?.screen?.height ?? 844}`,
                width: 'auto',
                maxWidth: '100%',
                margin: '0 auto',
              }}
            >
              {usesV2Stream ? (
                <canvas
                  ref={canvasRef}
                  data-testid="takeover-frame"
                  aria-label="live device view"
                  role="img"
                  aria-disabled={!open || gestureBusy}
                  draggable={false}
                  style={{ userSelect: 'none', touchAction: 'none' }}
                  className={`absolute inset-0 h-full w-full object-contain ${gestureBusy ? 'cursor-wait' : 'cursor-crosshair'}`}
                  onDragStart={(event) => event.preventDefault()}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerCancel={handlePointerCancel}
                  onPointerUp={handlePointerUp}
                />
              ) : (
                <img
                  data-testid="takeover-frame"
                  src={legacyStreamSrc ?? undefined}
                  alt="live device view"
                  aria-disabled={!open || gestureBusy}
                  referrerPolicy="no-referrer"
                  draggable={false}
                  style={{ userSelect: 'none', touchAction: 'none' }}
                  className={`absolute inset-0 h-full w-full object-contain ${gestureBusy ? 'cursor-wait' : 'cursor-crosshair'}`}
                  onDragStart={(event) => event.preventDefault()}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerCancel={handlePointerCancel}
                  onPointerUp={handlePointerUp}
                  onLoad={() => {
                    // A decoded multipart part firing `load` is the one health signal this
                    // transport has — reset the backoff so a later, unrelated outage starts over
                    // at 1s rather than picking up wherever an old outage left off.
                    legacyReconnectAttemptRef.current = 0;
                    setStreamNotice(null);
                    if (legacyBackoffTimerRef.current !== null) {
                      window.clearTimeout(legacyBackoffTimerRef.current);
                      legacyBackoffTimerRef.current = null;
                    }
                  }}
                  onError={() => {
                    // Nothing left to retry for once the takeover is no longer actionable — the
                    // effect above already clears an already-scheduled timer when `open` flips
                    // false, this refuses to schedule a new one in the same situation.
                    if (!open) return;
                    // A second `error` before the scheduled reconnect fires is not a new failure —
                    // the browser can raise more than one for the same broken source.
                    if (legacyBackoffTimerRef.current !== null) return;
                    const attempt = legacyReconnectAttemptRef.current;
                    legacyReconnectAttemptRef.current = attempt + 1;
                    note('live view dropped — reconnecting');
                    setStreamNotice(RECONNECTING_NOTICE);
                    legacyBackoffTimerRef.current = window.setTimeout(() => {
                      legacyBackoffTimerRef.current = null;
                      if (!openRef.current) return;
                      setStreamNotice(null);
                      setLegacyStreamEpoch((epoch) => epoch + 1);
                    }, reconnectDelayMs(attempt));
                  }}
                />
              )}
            </div>
          }

          {streamNotice ? (
            <p className="text-xs text-amber-700" role="status">
              {streamNotice}
            </p>
          ) : null}

          {status ? (
            <p
              data-testid="takeover-status"
              className={`text-xs font-medium ${takeover.state === 'answered' ? 'text-emerald-700' : 'text-amber-700'}`}
            >
              {status}
            </p>
          ) : (
            <>
              <p className="text-xs opacity-70" data-testid="takeover-howto">
                Click to tap. Press and drag to move a wheel.
              </p>
              {remaining ? (
                <p
                  className="text-xs opacity-70"
                  data-testid="takeover-remaining"
                >
                  <b>{remaining}</b> remaining — if it runs out, the case ends
                  blocked and the run continues.
                </p>
              ) : null}
            </>
          )}

          {/* The SET/GOAL/NOW verify contract, collapsed by default (spec: it reads as cryptic jargon
              next to the plain instruction above). Nothing here is deleted, only demoted — the goal box
              and both wording variants that used to sit under the frame keep their exact content, just
              inside a disclosure a human opens on demand. Rendered with `hidden` (not unmounted) so it
              stays queryable for anyone inspecting the run, e.g. after the takeover already settled. */}
          <div
            data-testid="takeover-verify-contract"
            className="rounded-md border border-border bg-muted/20"
          >
            <button
              type="button"
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] font-medium text-muted-foreground"
              onClick={() => setVerifyDetailsOpen((prev) => !prev)}
              aria-expanded={verifyDetailsOpen}
              aria-controls={verifyBodyId}
              data-testid="takeover-verify-toggle"
            >
              {verifyDetailsOpen ? (
                <ChevronDownIcon
                  className="h-3.5 w-3.5 shrink-0 opacity-60"
                  aria-hidden
                />
              ) : (
                <ChevronRightIcon
                  className="h-3.5 w-3.5 shrink-0 opacity-60"
                  aria-hidden
                />
              )}
              How your gesture is verified
            </button>
            <div
              id={verifyBodyId}
              hidden={!verifyDetailsOpen}
              className="flex flex-col gap-2 px-3 pb-3"
              data-testid="takeover-verify-body"
            >
              <div
                data-testid="takeover-goal"
                className="grid grid-cols-3 gap-2 rounded border border-border p-2"
              >
                <div>
                  <span className="block text-[10px] uppercase opacity-60">
                    set
                  </span>
                  <b className="font-mono text-lg">{goal.what}</b>
                </div>
                <div>
                  <span className="block text-[10px] uppercase opacity-60">
                    goal
                  </span>
                  <b className="font-mono text-lg">{goal.target}</b>
                </div>
                <div>
                  <span className="block text-[10px] uppercase opacity-60">
                    now
                  </span>
                  <b
                    className="font-mono text-lg"
                    data-testid="takeover-observed"
                  >
                    {goal.now}
                  </b>
                </div>
                <p
                  className="col-span-3 text-xs"
                  data-testid="takeover-verdict"
                >
                  {goal.verdict}
                </p>
              </div>
              <p className="text-xs opacity-70">
                {verifiesObservedEffect ? (
                  <>
                    A gesture that changes nothing is reported as failed. After
                    Submit, the worker checks for a visible change on the page
                    or device; the next test assertion decides whether the
                    intended action succeeded.
                  </>
                ) : (
                  <>
                    A gesture that changes nothing is reported as failed — the
                    agent will not pretend it worked. Submit is verified by
                    re-reading the device: reach the goal and the case
                    continues, miss it and the case ends blocked.
                  </>
                )}
              </p>
            </div>
          </div>

          {takeover.viewport && !isCaptchaTakeover(takeover) ? (
            <TakeoverTypingRow
              disabled={!open || gestureBusy}
              onType={(text) => gesture.mutate({ kind: 'type', text })}
            />
          ) : null}

          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="takeover-submit"
              disabled={!open || settle.isPending || gestureBusy}
              onClick={() => settle.mutate('submit')}
              className={goal.hit ? 'ring-2 ring-emerald-600' : undefined}
            >
              Submit
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="takeover-giveup"
              disabled={!open || settle.isPending}
              onClick={() => settle.mutate('give_up')}
            >
              Give up
            </Button>
          </div>

          {(takeover.results ?? []).length > 0 ? (
            <ul
              data-testid="takeover-results"
              className="font-mono text-[11px]"
            >
              {(takeover.results ?? []).slice(-6).map((result) => (
                <li
                  key={result.commandId}
                  className={
                    result.changed ? 'text-emerald-700' : 'text-amber-700'
                  }
                >
                  {gestureResultLine(result)}
                </li>
              ))}
            </ul>
          ) : null}

          {log.length > 0 ? (
            <ul
              data-testid="takeover-log"
              className="font-mono text-[11px] opacity-70"
            >
              {log.slice(0, 6).map((line, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
